import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  FormControl,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, ErrorMessage } from "formik";
import Sidebar from "./CourseSidebar";
import color from "../../../../components/utils/Colors";
import { coursePriceValidation } from "../../../../components/schema/schema";
import { currencies, amounts } from "../../../../components/utils/Data";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  updateCoursePrice,
  getOneDataFromTable,
} from "../../../../services/services";
import { useSnackbar } from "../../../../components/utils/SnackBar";

const CoursePrice = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  const courseId = (location.state as any)?.courseId;

  const [initialValues, setInitialValues] = useState({
    currency: "",
    amount: "",
  });

  useEffect(() => {
    const fetchCourseData = async () => {
      if (courseId) {
        try {
          const res = await getOneDataFromTable("course", courseId);
          if (res?.data?.data) {
            const courseData = res.data.data;
            setInitialValues({
              currency: courseData.unitOfPrice || "",
              amount: courseData.price ? courseData.price.toString() : "",
            });
          }
        } catch (error) {
          console.error("Failed to fetch course details:", error);
          showSnackbar("Failed to fetch course details", "error");
        }
      }
    };
    fetchCourseData();
  }, [courseId]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: isSmallScreen ? "5px" : "30px",
        gap: "1%",
        marginBottom: "0px",
        paddingBottom: "5%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      {/* <Sidebar /> */}

      <Box
        sx={{
          marginTop: isSmallScreen ? "25px" : "0px",
          flex: 1,
          pl: "3%",
          borderRadius: "16px",
        }}
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={coursePriceValidation}
          onSubmit={async (values) => {
            setLoading(true);
            try {
              await updateCoursePrice(courseId, {
                unitOfPrice: values.currency,
                price: values.amount,
              });
              // Pass courseId so the preview/publish page can publish the course
              navigate("/course-preview", { state: { courseId } });
            } catch (err: any) {
              showSnackbar(
                err.response?.data?.message ||
                  "An error occurred while updating the price.",
              );
              console.error("Course price update error:", err);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({ values, handleChange, handleBlur, touched, errors }) => (
            <Form>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    background: color.textColor,
                    textAlign: "center",
                    color: "white",
                    width: "fit-content",
                    margin: "auto",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    mb: 2,
                  }}
                >
                  Price your Course
                </Typography>

                <Typography
                  sx={{
                    color: color.textColor,
                    ml: 1,
                    fontSize: "20px",
                    fontWeight: "bold",
                    mb: -1,
                    textAlign: "start",
                    width: "100%",
                  }}
                >
                  Price Criteria
                </Typography>

                {/* Placeholder info banner */}
                <Box
                  sx={{
                    width: "100%",
                    height: 100,
                    backgroundColor: "#e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body1">
                    Set a competitive price for your course
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: "20px", width: "100%" }}>
                  {/* Currency */}
                  <FormControl fullWidth className="input-root2">
                    <Select
                      name="currency"
                      value={values.currency}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.currency && Boolean(errors.currency)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Currency
                      </MenuItem>
                      {currencies.map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage
                      name="currency"
                      className="error"
                      component="div"
                    />
                  </FormControl>

                  {/* Amount */}
                  <FormControl fullWidth className="input-root2">
                    <Select
                      name="amount"
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.amount && Boolean(errors.amount)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Amount
                      </MenuItem>
                      {amounts.map((amount) => (
                        <MenuItem key={amount.value} value={amount.value}>
                          {amount.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage
                      name="amount"
                      className="error"
                      component="div"
                    />
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "end",
                    width: "100%",
                  }}
                >
                  {/* Save & Next — submits, then navigates to preview */}
                  <Button type="submit" id="button" disabled={loading}>
                    {loading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={20} />
                        Saving…
                      </Box>
                    ) : (
                      "Save & Next"
                    )}
                  </Button>

                  {/* Home — navigate away without saving */}
                  {/* <Button
                    id="button"
                    type="button"
                    onClick={() => navigate("/")}
                    disabled={loading}
                  >
                    Home
                  </Button> */}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default CoursePrice;
